/**
 * @fileoverview Notification Drawer Component
 * @description Notification drawer with grouping, pagination, and actions
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationStore } from '../../stores/notificationStore';
import NotificationItem from './NotificationItem';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { Button } from '../ui/Button';
import { Bell, CheckCheck, Settings, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Group notifications by time
 */
const groupNotifications = (notifications) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const groups = {
    today: [],
    yesterday: [],
    older: []
  };

  notifications.forEach(notification => {
    const notificationDate = new Date(notification.createdAt);
    
    if (notificationDate >= today) {
      groups.today.push(notification);
    } else if (notificationDate >= yesterday) {
      groups.yesterday.push(notification);
    } else {
      groups.older.push(notification);
    }
  });

  return groups;
};

export const NotificationDrawer = ({ children, className }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    fetchNotifications,
    markAllAsRead,
    initialize,
    cleanup
  } = useNotificationStore();

  useEffect(() => {
    // Initialize notifications when component mounts
    initialize();

    // Cleanup on unmount
    return () => {
      cleanup();
    };
  }, [initialize, cleanup]);

  useEffect(() => {
    // Refresh when drawer opens
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleViewAll = () => {
    setIsOpen(false);
    // Detect if we're in CA portal or vendor portal
    const isCA = window.location.pathname.startsWith('/ca');
    navigate(isCA ? '/ca/notifications' : '/app/notifications');
  };

  const handleSettings = () => {
    setIsOpen(false);
    // Detect if we're in CA portal or vendor portal
    const isCA = window.location.pathname.startsWith('/ca');
    navigate(isCA ? '/ca/notifications?tab=settings' : '/app/notifications?tab=settings');
  };

  const groupedNotifications = groupNotifications(notifications);
  const hasNotifications = notifications.length > 0;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        {children || (
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'h-10 w-10 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all relative group',
              className
            )}
          >
            <Bell className="h-5 w-5 group-hover:scale-110 transition-transform" />
            {unreadCount > 0 && (
              <span className="absolute top-2 right-2 h-2.5 w-2.5 bg-red-600 rounded-full border-2 border-white ring-1 ring-white/50" />
            )}
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-96 max-h-[600px] rounded-2xl p-2 bg-white shadow-2xl border-slate-100 animate-in fade-in slide-in-from-top-2 duration-300 overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
          <DropdownMenuLabel className="text-xs font-semibold text-slate-500 uppercase tracking-widest">
            Notifications {unreadCount > 0 && `(${unreadCount})`}
          </DropdownMenuLabel>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                title="Mark all as read"
              >
                <CheckCheck className="w-4 h-4 text-slate-500" />
              </button>
            )}
            <button
              onClick={handleSettings}
              className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
              title="Notification settings"
            >
              <Settings className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        </div>

        <DropdownMenuSeparator className="bg-slate-50" />

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-2">
          {isLoading && notifications.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
            </div>
          ) : error ? (
            <div className="px-3 py-4 text-center">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          ) : !hasNotifications ? (
            <div className="px-3 py-8 text-center">
              <Bell className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="text-sm font-medium text-slate-500">No notifications</p>
              <p className="text-xs text-slate-400 mt-1">You're all caught up!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Today */}
              {groupedNotifications.today.length > 0 && (
                <div>
                  <div className="px-3 py-1">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                      Today
                    </p>
                  </div>
                  <div className="space-y-1">
                    {groupedNotifications.today.map(notification => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Yesterday */}
              {groupedNotifications.yesterday.length > 0 && (
                <div>
                  <div className="px-3 py-1">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                      Yesterday
                    </p>
                  </div>
                  <div className="space-y-1">
                    {groupedNotifications.yesterday.map(notification => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Older */}
              {groupedNotifications.older.length > 0 && (
                <div>
                  <div className="px-3 py-1">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                      Older
                    </p>
                  </div>
                  <div className="space-y-1">
                    {groupedNotifications.older.slice(0, 5).map(notification => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {hasNotifications && (
          <>
            <DropdownMenuSeparator className="bg-slate-50" />
            <div className="px-3 py-2">
              <button
                onClick={handleViewAll}
                className="w-full text-center text-xs font-medium text-blue-600 hover:text-blue-700 py-2 hover:bg-blue-50 rounded-lg transition-colors"
              >
                View All Notifications
              </button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationDrawer;
